import { useEffect, useRef, useState } from 'react';
import { Sheet } from '../../components/ui/Sheet.js';
import { Button } from '../../components/ui/Button.js';
import { useToast } from '../../hooks/useToast.js';
import { feedbackMailto } from '../../hooks/useFeedback.js';
import '../tickets/DetailSheet.css'; // .detail-form-field/-label/-input
import './FeedbackSheet.css';

/* ══════════════════════════════════════════
   Feedback-Formular → GitHub-Issue
   ──────────────────────────────────────────
   Öffnet sich am Bug-Knopf, wenn der Server die Funktion anbietet
   (GITHUB_FEEDBACK_TOKEN gesetzt). Sonst bleibt der Knopf bei der Mail.

   Mitgeschickt wird nur technischer Kontext, kein Name: Das steht unter dem
   Formular, damit niemand überrascht ist. Die Grenzen hier spiegeln die des
   Servers (backend feedbackIssue.js, GRENZEN) — der Server prüft trotzdem
   selbst.
   ══════════════════════════════════════════ */

const TITEL_MIN = 3;
const TITEL_MAX = 120;
const TEXT_MIN = 10;
const TEXT_MAX = 5000;

export function FeedbackSheet({ open, onClose, kontext, onSenden }) {
  const { show } = useToast();
  const [titel, setTitel] = useState('');
  const [text, setText] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState('');
  const titelRef = useRef(null);

  /* Das Sheet fokussiert seine Fläche; wer meldet, will aber sofort tippen. */
  useEffect(() => {
    if (open) setTimeout(() => titelRef.current?.focus(), 0);
  }, [open]);

  function schliessen() {
    if (sendet) return;
    setFehler('');
    onClose();
  }

  async function senden(e) {
    e.preventDefault();
    const t = titel.trim();
    const b = text.trim();
    if (t.length < TITEL_MIN) return setFehler('Bitte einen kurzen Titel angeben.');
    if (b.length < TEXT_MIN) return setFehler('Bitte beschreiben, was passiert ist.');

    setFehler('');
    setSendet(true);
    const res = await onSenden({
      title: t,
      description: b,
      context: {
        ...kontext,
        viewport: `${window.innerWidth}×${window.innerHeight}`,
        userAgent: navigator.userAgent,
      },
    });
    setSendet(false);

    if (res?.success) {
      show(`Danke! Gemeldet als #${res.data.number}.`, 'success');
      setTitel('');
      setText('');
      onClose();
    } else {
      // Die Eingabe bleibt stehen: Wer auf „per Mail" ausweicht, kann sie
      // von hier kopieren.
      setFehler(res?.data && typeof res.data === 'string' && res.data !== 'disabled'
        ? res.data
        : 'Das hat nicht geklappt.');
    }
  }

  return (
    <Sheet open={open} onClose={schliessen} title="Feedback / Problem melden">
      <form className="feedback-form" onSubmit={senden} noValidate>
        <div className="detail-form-field">
          <label className="detail-form-label" htmlFor="feedback-titel">
            Worum geht's?
          </label>
          <span className="ff-box">
            <input
              id="feedback-titel"
              ref={titelRef}
              className="detail-form-input"
              type="text"
              maxLength={TITEL_MAX}
              placeholder="z. B. Archiv klappt nicht zu"
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              disabled={sendet}
            />
          </span>
        </div>

        <div className="detail-form-field">
          <label className="detail-form-label" htmlFor="feedback-text">
            Was ist passiert, was hast du erwartet?
          </label>
          <span className="ff-box">
            <textarea
              id="feedback-text"
              className="detail-form-input feedback-text"
              rows={7}
              maxLength={TEXT_MAX}
              placeholder="Schritte, die zum Fehler führen, helfen am meisten."
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={sendet}
            />
          </span>
        </div>

        <p className="feedback-hinweis">
          Mitgeschickt werden Seite, Veranstaltung, Rolle, Fenstergröße und Browser,
          aber <strong>nicht dein Name</strong>. Wenn wir nachfragen sollen, schreib
          deine Kontaktdaten in die Beschreibung.
        </p>

        {fehler && (
          <p className="feedback-fehler" role="alert">
            {fehler}{' '}
            <a href={feedbackMailto()}>Stattdessen per Mail melden</a>
          </p>
        )}

        <div className="sheet-footer">
          <Button type="button" variant="outline" onClick={schliessen} disabled={sendet}>
            Abbrechen
          </Button>
          <Button type="submit" variant="fire" disabled={sendet}>
            {sendet ? 'Wird gesendet…' : 'Senden'}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

export default FeedbackSheet;
